import { useState, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import WaitingIndicator from "../../../general-ui/waiting-indicator/waiting-indicator";
import { useTypedSelector } from "../../../../store/reducers/use-typed-selector";
import Search from "../../../general-ui/search-bar/search";
import { getBaseAdminURL } from 'utils/utils';
import Icon, { COLORS, ICONS } from "../../../general-ui/icon";
import GradientText from '@general-ui/gradient-text/gradient-text';
import { OptionalComponent } from 'utils/optional-component';
import images from 'images';
import { isBrandTVChannel } from 'utils/project-type-guards';
import Paging from '@general-ui/paging/paging';
import { PATHNAMES } from 'utils/admin-routing-utils';

import './channel-selector-v2.scss';
import { useProjectDetailsEnabled } from '@admin/events/hooks/project-details';
import ChannelAvatar from './channel-avatar';
import { useChannelSelect } from './hooks/use-channel-select';
import { PAGE_LIMIT } from './constants';

export default function ChannelSelectorV2(): JSX.Element {
	const user = useTypedSelector(state => state.AuthReducer.user);
	const channels = useTypedSelector(state => state.AuthReducer.channels);
	const projectDetailsFlagOn = useProjectDetailsEnabled();
	const { selectChannel, sortedChannels, willReload } = useChannelSelect({
		onChannelSelectSuccess: () => window.location.href = getBaseAdminURL() + PATHNAMES.Events,
		doSort: true
	});

	const [showChannelsList, setShowChannelsList] = useState(false);
	const [showFullChannelList, setShowFullChannelList] = useState(false);
	const [searchCriteria, setSearchCriteria] = useState('');
	const [page, setPage] = useState(1);

	const currentChannel = useMemo(() =>
		channels.find(channel => channel.channel === user?.active_channel),
		[user, channels]
	);

	useEffect(() => {
		if (showFullChannelList) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}

		return () => {
			document.body.style.overflow = '';
		};
	}, [showFullChannelList]);

	const filteredChannels = useMemo(() => {
		if (!searchCriteria) return sortedChannels;
		return sortedChannels.filter(channel => channel.name.toLowerCase().includes(searchCriteria.toLowerCase()));
	}, [searchCriteria, sortedChannels]);

	useEffect(() => {
		if (!projectDetailsFlagOn) {
			return;
		}

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
				setShowFullChannelList(true);
				event.preventDefault();
			}

			if (showFullChannelList && event.key === 'Escape') {
				setShowFullChannelList(false);
			}
		};

		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [projectDetailsFlagOn, showFullChannelList]);

	if (!projectDetailsFlagOn) {
		return <></>;
	}

	if (!user || willReload) {
		return <WaitingIndicator />;
	}

	return (
		<>
			<div
				className={classNames('channel-selector-v2', { 'open': showChannelsList })}
				onClick={() => setShowChannelsList(!showChannelsList)}
			>
				{currentChannel
					? <ChannelAvatar channel={currentChannel} />
					: null}

				{showChannelsList
					? <Icon name={ICONS.KEYBOARD_ARROW_UP} color={COLORS.WHITE} size={12} />
					: <Icon name={ICONS.KEYBOARD_ARROW_DOWN} color={COLORS.WHITE} size={12} />}

				<div className={classNames('channels-list', { open: showChannelsList })}>
					{sortedChannels.slice(0, 5).map(channel => (
						<div
							className="channel-item"
							key={channel.uuid}
							onClick={() => user.active_channel !== channel.channel ? selectChannel(channel) : null}
						>
							<div className="left">
								<ChannelAvatar channel={channel} />
								<div className="channel-name" title={channel.name}>
									{channel.name}
								</div>
							</div>
							<OptionalComponent display={user.is_super_admin}>
								<div className="channel-type">
									<GradientText gradient="--gradient-sherbet-3">
										{isBrandTVChannel(channel.type) ? 'Brandlive' : 'Webinar+'}
									</GradientText>
								</div>
							</OptionalComponent>
						</div>
					))}

					<OptionalComponent display={channels.length > 5}>
						<div className="search-button" onClick={() => setShowFullChannelList(true)}>
							<Icon name={ICONS.SEARCH} color={COLORS.WHITE} size={12} />
							<div className="primary-text">Search All Channels</div>
							<div className="secondary-text">{channels.length}</div>
						</div>
					</OptionalComponent>
				</div>
			</div>
			<OptionalComponent display={showFullChannelList}>
				<div className={classNames('channel-list-overlay', { 'no-results': !filteredChannels.length })}>
					<div className="close-button" onClick={() => setShowFullChannelList(false)}>
						<Icon name={ICONS.CLOSE} color={COLORS.WHITE} size={12} />
					</div>

					<div className="channel-search-container">
						<img className="search-spotlight" src={images.RectangularSpotlight} />
						<Search
							onSearch={value => {
								setSearchCriteria(value);
								setPage(1);
							}}
							onClear={() => {
								setSearchCriteria('');
								setPage(1);
							}}
							onChange={value => {
								setSearchCriteria(value);
								setPage(1);
							}}
							autoFocus
							placeholder="Search channel..."
						/>
					</div>

					<div className="full-channel-list">
						{filteredChannels.slice((page - 1) * PAGE_LIMIT, page * PAGE_LIMIT).map(channel => (
							<div
								className="full-channel-list-item"
								key={channel.uuid}
								onClick={() => user.active_channel !== channel.channel ? selectChannel(channel) : null}
							>
								<ChannelAvatar channel={channel} />
								<div className="channel-name" title={channel.name}>
									{channel.name}
								</div>
								<div className="channel-event-count">
									{channel.event_count} Project{channel.event_count === 1 ? '' : 's'}
								</div>
								{/* Removed from designs but probably comes back */}
								{/* <div className="channel-type">
									{isTownhallChannel(channel.type) ? 'Brandlive' : 'Webinar+'}
								</div> */}
							</div>
						))}
					</div>

					<OptionalComponent display={!filteredChannels.length}>
						<div className="no-channel-results">
							<img className="background-gradient" src={images.PinkCircularGradient} />
							<img className="telly-tired" src={images.TellyTired} />
							<div className="primary-text">Sorry, no results found</div>
							<div className="secondary-text">We couldn't find any matches for "{searchCriteria}"</div>
						</div>
					</OptionalComponent>

					<OptionalComponent display={filteredChannels.length > PAGE_LIMIT}>
						<Paging
							page={page}
							setPage={setPage}
							totalPages={Math.ceil(filteredChannels.length / PAGE_LIMIT)}
						/>
					</OptionalComponent>
				</div>
			</OptionalComponent>
		</>
	);
}
